// Lender-logo registry + a tiny `LenderLogo` component.
// Every logo lives under /public/lender-logos so we can serve it as a static
// asset with no bundler round-trip.
//
// The registry is looked up by a normalised key (lowercase, alphanumerics only)
// which lets us tolerate every casing variant that lives in DB across old
// packs — "Bluestone", "bluestone", "MA Money", "ma_money", "MA-Money" all
// resolve to the same logo.
import React from 'react';

// key → { src, palette, label }
export const LENDER_LOGOS = {
  bankwest:  { src: '/lender-logos/bankwest.png',  bg: '#f8a34a', label: 'Bankwest' },
  bluestone: { src: '/lender-logos/bluestone.jpg', bg: '#ffffff', label: 'Bluestone Home Loans' },
  mamoney:   { src: '/lender-logos/ma-money.jpg',  bg: '#ffffff', label: 'MA Money' },
  macquarie: { src: '/lender-logos/macquarie.png', bg: '#ffffff', label: 'Macquarie' },
  bendigo:   { src: '/lender-logos/bendigo.webp',  bg: '#ffffff', label: 'Bendigo Bank' },
};

const _norm = (raw) => (raw || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');

// Look up the logo record for a lender name — returns null if unmapped.
export function lookupLenderLogo(lenderName) {
  const key = _norm(lenderName);
  if (!key) return null;
  if (LENDER_LOGOS[key]) return LENDER_LOGOS[key];
  // Reasonable fuzzy fallbacks
  if (key.includes('bankwest')) return LENDER_LOGOS.bankwest;
  if (key.includes('bluestone')) return LENDER_LOGOS.bluestone;
  if (key.includes('mamoney'))   return LENDER_LOGOS.mamoney;
  if (key.includes('macquarie')) return LENDER_LOGOS.macquarie;
  if (key.includes('bendigo'))   return LENDER_LOGOS.bendigo;
  return null;
}

/**
 * <LenderLogo lender="Bankwest" size="md" /> renders a small rounded logo tile.
 * Falls back to a coloured initials pill if we don't have a logo for that lender.
 *
 * Props:
 *  - lender     : string (case-insensitive)
 *  - size       : 'xs' | 'sm' | 'md' | 'lg'  (default 'md')
 *  - showLabel  : boolean — appends the lender name beside the logo
 *  - className  : extra classes for the outer wrapper
 */
export default function LenderLogo({ lender, size = 'md', showLabel = false, className = '', testid }) {
  const logo = lookupLenderLogo(lender);
  const dims = {
    xs: { box: 'h-5',      pad: 'px-1.5', text: 'text-[10px]' },
    sm: { box: 'h-6',      pad: 'px-2',   text: 'text-[11px]' },
    md: { box: 'h-8',      pad: 'px-2.5', text: 'text-[12px]' },
    lg: { box: 'h-10',     pad: 'px-3',   text: 'text-sm'     },
  }[size] || { box: 'h-8', pad: 'px-2.5', text: 'text-[12px]' };

  if (logo) {
    return (
      <div
        className={`inline-flex items-center ${dims.box} rounded-md overflow-hidden shrink-0 border border-slate-200 ${className}`}
        style={{ background: logo.bg }}
        title={logo.label}
        data-testid={testid || `lender-logo-${lender?.toString().toLowerCase()}`}
      >
        <img
          src={logo.src}
          alt={logo.label}
          className={`${dims.box} w-auto object-contain ${dims.pad}`}
          style={{ maxWidth: 160 }}
          draggable={false}
        />
        {showLabel && (
          <span className={`${dims.text} font-black pr-2`} style={{ color: '#0f172a' }}>
            {logo.label}
          </span>
        )}
      </div>
    );
  }

  // Fallback initials pill so unmapped lenders still show something readable.
  const initials = (lender || '?').toString().trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
  return (
    <div
      className={`inline-flex items-center justify-center ${dims.box} ${dims.pad} rounded-md shrink-0 ${className}`}
      style={{ background: '#e0e7ff', color: '#3730a3' }}
      title={lender}
      data-testid={testid || `lender-logo-${_norm(lender) || 'unknown'}`}
    >
      <span className={`${dims.text} font-black tabular-nums`}>{initials}</span>
    </div>
  );
}
